import type { QuickInterviewRole, QuickInterviewTurn } from "./api";
import { sanitizeCandidateTranscript } from "./transcriptGuard";

export const INTERVIEWER_TRANSCRIPT_FALLBACK =
  "面接官の発話が文字起こしされませんでした。";
export const CANDIDATE_TRANSCRIPT_FALLBACK =
  "回答の文字起こしが完了しませんでした。";
export const TRANSCRIPTION_RETRY_NOTICE =
  "回答の文字起こしに失敗しました。もう一度回答してください。";
const TRUNCATED_TRANSCRIPT_SUFFIX = "\n（文字数上限のため一部省略しました）";
export const FALLBACK_MAX_TURN_TRANSCRIPT_LENGTH = 10_000;

export type RealtimeSignal =
  | "session_ready"
  | "candidate_started"
  | "candidate_stopped"
  | "candidate_transcript"
  | "candidate_transcript_rejected"
  | "interviewer_started"
  | "interviewer_transcript"
  | "response_done"
  | "transcription_failed"
  | "error";

export interface RealtimeUpdate {
  signal: RealtimeSignal | null;
  turns: QuickInterviewTurn[];
  candidateCount: number;
  responseInProgress: boolean;
  errorMessage?: string;
  itemId?: string;
  responseId?: string;
}

interface SnapshotOptions {
  includePendingCandidateFallbacks?: boolean;
}

export interface RealtimeConversationTrackerOptions {
  maxTurnTranscriptLength?: number;
}

interface RealtimeItem {
  id?: unknown;
  role?: unknown;
  content?: unknown;
}

interface RealtimeEvent {
  type?: unknown;
  item_id?: unknown;
  previous_item_id?: unknown;
  response_id?: unknown;
  transcript?: unknown;
  item?: RealtimeItem;
  response?: { id?: unknown };
  error?: { message?: unknown };
}

function isRole(value: unknown): value is QuickInterviewRole {
  return value === "interviewer" || value === "candidate";
}

function realtimeRole(value: unknown): QuickInterviewRole | null {
  if (value === "assistant") return "interviewer";
  if (value === "user") return "candidate";
  return isRole(value) ? value : null;
}

// 面接官の応答の生死は、応答の識別子で管理する。
// 音声や文字起こしの断片だけで実行中を判定すると、応答が完了した後に
// 遅れて届いた断片が実行中へ戻し、完了待ちの処理が永久に走らなくなる。
//
//   応答A開始 ──> 実行中(A)
//   断片(A)   ──> 実行中(A) のまま、発話中を通知する
//   完了(A)   ──> 実行中なし、A を完了済みに記録する
//   断片(A)   ──> 完了済みなので無視する
//
//   応答B開始 ──> 実行中(B)
//   完了(A)   ──> A を完了済みに記録するだけ。実行中(B) は消さない
//   断片(?)   ──> 識別子が読めないので実行中フラグにも通知にも触れない
export class RealtimeConversationTracker {
  private itemOrder: string[] = [];
  private roles = new Map<string, QuickInterviewRole>();
  private transcripts = new Map<string, string>();
  private pendingCandidateIds = new Set<string>();
  private pendingInterviewerIds = new Set<string>();
  private completedInterviewerIdsWithoutTranscript = new Set<string>();
  private responseActive = false;
  private activeResponseId: string | null = null;
  private finishedResponseIds = new Set<string>();
  private maxTurnTranscriptLength: number;

  constructor(options?: RealtimeConversationTrackerOptions) {
    const maxTurnTranscriptLength = options?.maxTurnTranscriptLength;
    this.maxTurnTranscriptLength =
      typeof maxTurnTranscriptLength === "number" &&
      Number.isFinite(maxTurnTranscriptLength) &&
      maxTurnTranscriptLength > TRUNCATED_TRANSCRIPT_SUFFIX.length
        ? maxTurnTranscriptLength
        : FALLBACK_MAX_TURN_TRANSCRIPT_LENGTH;
  }

  handle(rawEvent: string | object): RealtimeUpdate {
    let event: RealtimeEvent;
    try {
      event =
        typeof rawEvent === "string"
          ? (JSON.parse(rawEvent) as RealtimeEvent)
          : (rawEvent as RealtimeEvent);
    } catch {
      return this.update("error", "音声イベントの読み取りに失敗しました。");
    }

    const type = typeof event.type === "string" ? event.type : "";
    let signal: RealtimeSignal | null = null;
    let errorMessage: string | undefined;
    let relatedItemId: string | undefined;
    let relatedResponseId: string | undefined;

    switch (type) {
      case "session.created":
      case "session.updated":
        signal = "session_ready";
        break;
      case "conversation.item.added":
      case "conversation.item.created": {
        const itemId = this.stringValue(event.item?.id);
        const role = realtimeRole(event.item?.role);
        if (itemId && role) {
          this.registerItem(
            itemId,
            role,
            this.stringValue(event.previous_item_id),
          );
          if (role === "candidate") {
            const transcript = this.inputTextFromContent(event.item?.content);
            if (transcript && !this.transcripts.has(itemId)) {
              this.pendingCandidateIds.delete(itemId);
              this.transcripts.set(itemId, this.limitTranscript(transcript));
              relatedItemId = itemId;
              signal = "candidate_transcript";
            }
          }
        }
        break;
      }
      case "input_audio_buffer.speech_started": {
        const itemId = this.stringValue(event.item_id);
        if (itemId) {
          this.registerItem(itemId, "candidate");
          this.pendingCandidateIds.add(itemId);
        }
        signal = "candidate_started";
        break;
      }
      case "input_audio_buffer.speech_stopped": {
        const itemId = this.stringValue(event.item_id);
        if (itemId) {
          this.registerItem(itemId, "candidate");
          this.pendingCandidateIds.add(itemId);
        }
        signal = "candidate_stopped";
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const itemId = this.stringValue(event.item_id);
        const transcript = this.stringValue(event.transcript)?.trim();
        if (itemId) {
          this.pendingCandidateIds.delete(itemId);
          relatedItemId = itemId;
          // 無音・ノイズ由来の幻覚文字起こしや、文字起こし指示文の混入は
          // 回答から取り除く。取り除いた結果が空なら回答として扱わず、
          // 会話履歴からも取り除く（クライアント側で削除イベントを送る）。
          // トラッカー段階で除外することで、回答数・回答上限のターン固定・
          // 代替文言の補完・完了時の保存のいずれにも幻覚が混入しない
          const sanitized = sanitizeCandidateTranscript(transcript);
          if (sanitized) {
            this.registerItem(itemId, "candidate");
            this.transcripts.set(itemId, this.limitTranscript(sanitized));
            signal = "candidate_transcript";
          } else {
            this.removeItem(itemId);
            signal = "candidate_transcript_rejected";
          }
        }
        break;
      }
      case "conversation.item.input_audio_transcription.failed": {
        const itemId = this.stringValue(event.item_id);
        if (itemId) this.pendingCandidateIds.delete(itemId);
        signal = "transcription_failed";
        errorMessage = TRANSCRIPTION_RETRY_NOTICE;
        break;
      }
      case "response.created": {
        const responseId = this.stringValue(event.response?.id);
        this.activeResponseId = responseId ?? null;
        this.responseActive = true;
        relatedResponseId = responseId;
        signal = "interviewer_started";
        break;
      }
      case "response.output_audio.delta":
      case "response.audio.delta":
      case "response.output_audio_transcript.delta": {
        const responseId = this.stringValue(event.response_id);
        const fromFinishedResponse = responseId
          ? this.finishedResponseIds.has(responseId)
          : false;
        const itemId = this.stringValue(event.item_id);
        if (itemId) {
          this.registerItem(itemId, "interviewer");
          // 完了済みの応答の断片を文字起こし待ちへ戻すと、次の応答の完了時に
          // 別の応答の項目として代替文言を補ってしまう
          if (!fromFinishedResponse) this.pendingInterviewerIds.add(itemId);
        }

        // 項目の記録は続けるが、完了済みの応答から遅れて届いた断片では
        // 実行中フラグにも通知にも触れない。識別子が読めない断片も同じ扱いにし、
        // 実行中へ戻す側には倒さない
        if (!responseId || fromFinishedResponse) break;

        this.activeResponseId ??= responseId;
        this.responseActive = true;
        relatedResponseId = responseId;
        signal = "interviewer_started";
        break;
      }
      case "response.output_audio_transcript.done": {
        const itemId = this.stringValue(event.item_id);
        const transcript = this.stringValue(event.transcript)?.trim();
        if (itemId) {
          this.registerItem(itemId, "interviewer");
          this.pendingInterviewerIds.delete(itemId);
          relatedItemId = itemId;
          if (transcript) {
            this.completedInterviewerIdsWithoutTranscript.delete(itemId);
            this.transcripts.set(itemId, this.limitTranscript(transcript));
          } else {
            this.completedInterviewerIdsWithoutTranscript.add(itemId);
          }
        }
        signal = "interviewer_transcript";
        break;
      }
      case "response.done": {
        for (const itemId of this.pendingInterviewerIds) {
          if (!this.transcripts.has(itemId)) {
            this.completedInterviewerIdsWithoutTranscript.add(itemId);
          }
        }
        this.pendingInterviewerIds.clear();

        const responseId =
          this.stringValue(event.response?.id) ??
          this.stringValue(event.response_id);
        if (responseId) this.finishedResponseIds.add(responseId);
        // 別の応答が始まった後に古い応答の完了が遅れて届いても、
        // 現在実行中の応答を終わったことにしない。守るべき識別子が無いときは、
        // 実行中のまま取り残さないよう解除する
        if (
          !responseId ||
          this.activeResponseId === null ||
          responseId === this.activeResponseId
        ) {
          this.activeResponseId = null;
          this.responseActive = false;
        }
        relatedResponseId = responseId;
        signal = "response_done";
        break;
      }
      case "error":
        signal = "error";
        errorMessage =
          this.stringValue(event.error?.message) ??
          "音声接続でエラーが発生しました。";
        break;
    }

    return this.update(
      signal,
      errorMessage,
      undefined,
      relatedItemId,
      relatedResponseId,
    );
  }

  snapshot(options?: SnapshotOptions): RealtimeUpdate {
    return this.update(null, undefined, options);
  }

  hasPendingCandidateTranscripts() {
    return this.pendingCandidateIds.size > 0;
  }

  pendingCandidateItemIds() {
    return [...this.pendingCandidateIds];
  }

  transcriptForItem(itemId: string) {
    return this.transcripts.get(itemId) ?? null;
  }

  mergedTurnKeyForItem(itemId: string) {
    const mergedTurn = this.buildMergedTurns().find((turn) =>
      turn.itemIds.includes(itemId),
    );
    return mergedTurn?.itemIds[0] ?? null;
  }

  private removeItem(itemId: string) {
    this.itemOrder = this.itemOrder.filter((id) => id !== itemId);
    this.roles.delete(itemId);
    this.transcripts.delete(itemId);
    this.pendingCandidateIds.delete(itemId);
  }

  private registerItem(
    itemId: string,
    role: QuickInterviewRole,
    previousItemId?: string,
  ) {
    this.roles.set(itemId, role);
    if (this.itemOrder.includes(itemId)) return;

    if (previousItemId) {
      const previousIndex = this.itemOrder.indexOf(previousItemId);
      if (previousIndex >= 0) {
        this.itemOrder.splice(previousIndex + 1, 0, itemId);
        return;
      }
    }
    this.itemOrder.push(itemId);
  }

  private buildMergedTurns(options?: SnapshotOptions) {
    const turns = this.itemOrder.flatMap((itemId) => {
      const role = this.roles.get(itemId);
      const transcript =
        this.transcripts.get(itemId) ??
        (role === "candidate" &&
        options?.includePendingCandidateFallbacks &&
        this.pendingCandidateIds.has(itemId)
          ? CANDIDATE_TRANSCRIPT_FALLBACK
          : undefined) ??
        (role === "interviewer" &&
        this.completedInterviewerIdsWithoutTranscript.has(itemId)
          ? INTERVIEWER_TRANSCRIPT_FALLBACK
          : undefined);
      if (!role || !transcript) return [];
      return [{ itemIds: [itemId], role, transcript }];
    });

    return turns.reduce<typeof turns>((result, turn) => {
      if (result.length === 0) {
        if (turn.role === "interviewer") result.push(turn);
        return result;
      }

      const previous = result[result.length - 1];
      if (previous.role === turn.role) {
        result[result.length - 1] =
          previous.role === "candidate"
            ? {
                itemIds: [...previous.itemIds, ...turn.itemIds],
                role: previous.role,
                transcript: this.mergeTranscripts(
                  previous.transcript,
                  turn.transcript,
                ),
              }
            : turn;
      } else {
        result.push(turn);
      }
      return result;
    }, []);
  }

  private update(
    signal: RealtimeSignal | null,
    errorMessage?: string,
    options?: SnapshotOptions,
    itemId?: string,
    responseId?: string,
  ) {
    const normalizedTurns: QuickInterviewTurn[] = this.buildMergedTurns(
      options,
    ).map((turn, position) => ({
      provider_item_id: turn.itemIds[0],
      role: turn.role,
      transcript: turn.transcript,
      position,
    }));
    return {
      signal,
      turns: normalizedTurns,
      candidateCount: normalizedTurns.filter(
        (turn) => turn.role === "candidate",
      ).length,
      responseInProgress: this.responseActive,
      errorMessage,
      itemId,
      responseId,
    };
  }

  private stringValue(value: unknown) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
  }

  private inputTextFromContent(content: unknown) {
    if (!Array.isArray(content)) return null;

    const text = content.find((part) => {
      if (!part || typeof part !== "object") return false;
      return (part as { type?: unknown }).type === "input_text";
    });
    if (!text || typeof text !== "object") return null;

    const value = (text as { text?: unknown }).text;
    return typeof value === "string" && value.trim().length > 0
      ? value.trim()
      : null;
  }

  private limitTranscript(transcript: string) {
    if (transcript.length <= this.maxTurnTranscriptLength) return transcript;

    return `${transcript.slice(
      0,
      this.maxTurnTranscriptLength - TRUNCATED_TRANSCRIPT_SUFFIX.length,
    )}${TRUNCATED_TRANSCRIPT_SUFFIX}`;
  }

  private mergeTranscripts(previous: string, next: string) {
    return this.limitTranscript(`${previous}\n${next}`);
  }
}
